import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { FormikProvider, getIn, useFormik } from "formik";
import { Add, Inventory2Outlined } from "@mui/icons-material";
import FmdGoodOutlinedIcon from '@mui/icons-material/FmdGoodOutlined';
import { Typography } from "@mui/material";
import { useSelector } from "react-redux";
import Card from "../../../../Card";
import messages from "../../../../../assets/locale/messages";
import useRevalidateOnLocaleChange from "../../../../../hooks/useRevalidateOnLocaleChange";
import {
  UIAccordion,
  UIButton,
  UIIcon,
  UINumberField,
  UISelect,
  UITextField,
  UITextarea,
  UIUpload,
} from "../../../../ui";
import { RemoveModal } from "../../../../modals";
import Tooltip from "../../../../Tooltip";
import {
  buildTouchedZonesAndRequirements,
  createRequirement,
  createZone,
  getManagerOptions,
  getRequirementOptions,
  getAvailableRequirementOptions,
  getSelectedRequirementOption,
  resolveRequirementRowSelectionKey,
  getVisibleError,
  sectionErrorsApplyTo,
} from "./helpers";
import getZonesAndLogisticalRequirementsValidationSchema from "./validationSchema";
import {
  getPortalZonesStepDraftValidationSchema,
  getPortalZonesStepFullValidationSchema,
} from "./portalValidationSchema";
import {
  buildPortalStepThreePayload,
  mapPortalEventToZonesFormValues,
  mergePersistedPortalRequirementsIntoFormValues,
  mergePersistedPortalZonesIntoFormValues,
  resolvePortalZonesImageUrl,
} from "../../../../../utils/companyPortal/eventApi";
import {
  countFormErrors,
  focusFirstError,
  getFirstErrorPath,
} from "../validationFocus";
import {
  clearTouchedListItem,
  removeTouchedListItem,
} from "../touchedListHelpers";
import { clx, showSuccessMsg, showWarningMsg } from "../../../../../utils/Helpers";
import { eventStatuses } from "../../../../../utils/Constants";
import useEventSupervisorOptions from "../../../../../hooks/useEventSupervisorOptions";
import { getSupervisorOptionsForZone } from "../../../../../utils/supervisorOptions";
import { canListEventRequests } from "../../../../../utils/requestApis";

import "./ZonesAndLogisticalRequirements.scss";

const ZonesAndLogisticalRequirements = ({
  isEditMode,
  registerSubmit,
  registerStepApi,
  companyPortalMode,
  initialEvent,
  requirementsOptions,
  supervisorsOptions,
  requestsPortal,
  onDeleteRequirement,
}) => {
  const lang = useSelector((state) => state.locale.lang);
  const login = useSelector((state) => state.auth?.login);
  const { events, inputsValidations, shared } = messages[lang];
  const t = events?.creatEvent?.zonesAndLogisticalRequirements;
  const projectManagers = useSelector(
    (state) => state.lookups?.projectManagers || []
  );

  const managerOptions = useMemo(
    () => getManagerOptions(projectManagers),
    [projectManagers]
  );
  const requirementOptions = useMemo(
    () => getRequirementOptions(requirementsOptions),
    [requirementsOptions]
  );
  const validationMessages = useMemo(
    () => ({
      required: inputsValidations.generalFieldRequired,
      nonNegative: inputsValidations.onlyPositiveNumbersWithZero,
      maxTwoHundredChars: inputsValidations.maxTwoHundredChars,
      maxHundredChars: inputsValidations.maxHundredChars,
      maxFiftyChars: inputsValidations.maxFiftyChars,
      zoneRequired: t.zoneRequired,
      requirementRequired: t.requirementRequired,
      zoneMapRequired: inputsValidations.generalFieldRequiredToPublish,
    }),
    [inputsValidations, t]
  );
  const portalZonesDraftValidationSchema = useMemo(
    () => getPortalZonesStepDraftValidationSchema(validationMessages),
    [validationMessages]
  );
  const portalZonesFullValidationSchema = useMemo(
    () =>
      getPortalZonesStepFullValidationSchema(
        validationMessages,
        requirementOptions,
      ),
    [validationMessages, requirementOptions]
  );
  const validationSchema = useMemo(
    () =>
      companyPortalMode
        ? portalZonesFullValidationSchema
        : getZonesAndLogisticalRequirementsValidationSchema(
            validationMessages,
            requirementOptions,
          ),
    [
      companyPortalMode,
      portalZonesFullValidationSchema,
      validationMessages,
      requirementOptions,
    ],
  );

  const [expandedZones, setExpandedZones] = useState({ 0: true });
  const [validationAttempt, setValidationAttempt] = useState(0);
  // Zones that existed at the last continue attempt; zones added afterwards
  // stay error-free until they're touched or the next attempt (PROLINE-2514).
  const [validatedZoneCount, setValidatedZoneCount] = useState(0);
  const [zonePendingRemoval, setZonePendingRemoval] = useState(null);
  const [requirementPendingRemoval, setRequirementPendingRemoval] = useState(null);
  const [zoneMapPreviewDataUrl, setZoneMapPreviewDataUrl] = useState(null);
  const [zoneMapUploadCompleted, setZoneMapUploadCompleted] = useState(false);
  const [portalZoneMapSubmitLoading, setPortalZoneMapSubmitLoading] =
    useState(false);
  const [portalZoneMapUploadPercent, setPortalZoneMapUploadPercent] =
    useState(null);

  /**
   * Server-persisted zones removed in this session.
   * Merged into buildPayload as `{ id, _destroy: true }` on next PATCH.
   */
  const deletedZonesRef = useRef([]);

  /**
   * Server-persisted requirements removed in this session.
   * Merged into buildPayload as `{ id, _destroy: true }` on Save / Finish.
   */
  const deletedRequirementsRef = useRef([]);

  const formInitialValues = useMemo(() => {
    if (companyPortalMode && initialEvent) {
      return mapPortalEventToZonesFormValues(initialEvent);
    }
    return {
      zones_attributes: [createZone()],
      logistics_requirements_attributes: [],
      zones_image_url: "",
    };
  }, [companyPortalMode, initialEvent]);

  const formik = useFormik({
    initialValues: formInitialValues,
    enableReinitialize: true,
    validationSchema,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: () => {},
  });

  useRevalidateOnLocaleChange(() => {
    void formik.validateForm();
  });

  useEffect(() => {
    if (typeof formik.values.zones_image_url === "string") {
      setZoneMapPreviewDataUrl(null);
    }
  }, [formik.values.zones_image_url]);

  useEffect(() => {
    const url = formInitialValues.zones_image_url;
    setZoneMapUploadCompleted(
      typeof url === "string" && String(url).trim().length > 0,
    );
  }, [formInitialValues.zones_image_url]);

  const zones = formik.values.zones_attributes;
  const requirements = formik.values.logistics_requirements_attributes;
  const selectedRequirementKeys = (requirements || [])
    .map((row) => resolveRequirementRowSelectionKey(row, requirementOptions))
    .filter(Boolean)
    .join("|");

  /**
   * Legacy ZonesFields: hide supervisor on create while event is still pending.
   * Company edit: always use supervisor dropdown (same as admin) once event exists.
   */
  const showSupervisorField = useMemo(() => {
    if (!companyPortalMode || !initialEvent?.id) return false;

    const status = initialEvent?.status;
    if ([eventStatuses.cancelled, eventStatuses.past].includes(status)) {
      return false;
    }

    if (requestsPortal === "company" && isEditMode) return true;

    return status !== eventStatuses.draft;
  }, [
    companyPortalMode,
    initialEvent?.id,
    initialEvent?.status,
    isEditMode,
    requestsPortal,
  ]);

  const allSupervisorSelectOptions = useEventSupervisorOptions({
    eventId: initialEvent?.id,
    requestsPortal: requestsPortal || "admin",
    initialEvent,
    // Without it, only the supervisors already on the zones are listed.
    enabled: showSupervisorField && canListEventRequests(requestsPortal, login),
    extraOptions: supervisorsOptions,
    lang,
  });

  const setZones = (nextZones) => {
    formik.setFieldValue("zones_attributes", nextZones);
  };

  const setRequirements = (nextRequirements) => {
    formik.setFieldValue("logistics_requirements_attributes", nextRequirements);
  };

  const handleAddZone = () => {
    const nextZones = [...zones, createZone()];
    setZones(nextZones);
    formik.setTouched(
      clearTouchedListItem(
        formik.touched,
        "zones_attributes",
        nextZones.length - 1
      ),
      false
    );
    setExpandedZones((current) => ({
      ...current,
      [nextZones.length - 1]: true,
    }));
  };

  const handleRemoveZone = () => {
    if (zonePendingRemoval === null) return;

    const removedZone = zones[zonePendingRemoval];
    const persistedZone = removedZone?.id != null && removedZone.id !== "";

    // Only update the form's local state here — the actual deletion is sent
    // to the server with the rest of the step's changes on Save/Finish, so an
    // in-progress zone map or requirement edit isn't lost or persisted early.
    if (persistedZone) {
      deletedZonesRef.current = [
        ...deletedZonesRef.current,
        { id: removedZone.id, _destroy: true },
      ];
    }

    setZones(zones.filter((_zone, index) => index !== zonePendingRemoval));
    formik.setTouched(
      removeTouchedListItem(
        formik.touched,
        "zones_attributes",
        zonePendingRemoval
      ),
      false
    );
    if (zonePendingRemoval < validatedZoneCount) {
      setValidatedZoneCount((count) => count - 1);
    }
    setExpandedZones({ [Math.max(0, zonePendingRemoval - 1)]: true });
    setZonePendingRemoval(null);
    if (persistedZone) {
      showWarningMsg(t.zoneRemovalPendingSave);
    } else {
      showSuccessMsg(t.zoneRemoved, true);
    }
  };

  const handleManagerChange = (zoneIndex, manager) => {
    const path = `zones_attributes.${zoneIndex}`;
    formik.setFieldValue(`${path}.manager_id`, manager?.id || "");
    formik.setFieldValue(`${path}.manager_name`, manager?.name || "");
    formik.setFieldValue(`${path}.manager_email`, manager?.email || "");
    formik.setFieldValue(`${path}.manager_phone`, manager?.phone || "");
  };

  const handleAddRequirement = () => {
    setRequirements([...requirements, createRequirement()]);
  };

  const handleRemoveRequirement = () => {
    if (requirementPendingRemoval === null) return;

    const removedRequirement = requirements[requirementPendingRemoval];

    if (removedRequirement?.id) {
      const destroyRow = { id: removedRequirement.id, _destroy: true };
      if (onDeleteRequirement) {
        // Immediate API call — server already handles the deletion, so do NOT
        // queue it in the ref or buildPayload will send it again and get a 404.
        onDeleteRequirement(destroyRow);
      } else {
        deletedRequirementsRef.current = [
          ...deletedRequirementsRef.current,
          destroyRow,
        ];
      }
    }

    const nextRequirements = requirements.filter(
      (_req, index) => index !== requirementPendingRemoval
    );
    setRequirements(nextRequirements);
    setRequirementPendingRemoval(null);
    showSuccessMsg(
      removedRequirement?.id
        ? t.requirementRemovedSaveToApply
        : t.requirementRemoved,
      true,
    );
  };

  useEffect(() => {
    const validateContinue = async () => {
      const errors = await formik.validateForm();
      setValidatedZoneCount(formik.values.zones_attributes.length);
      formik.setTouched(
        buildTouchedZonesAndRequirements(formik.values, {
          companyPortalMode,
        }),
        false
      );
      const isValid = Object.keys(errors).length === 0;
      if (!isValid) {
        const firstErrorPath = getFirstErrorPath(errors);
        const zoneMatch = /^zones_attributes\.(\d+)/.exec(firstErrorPath);
        if (zoneMatch) {
          setExpandedZones((current) => ({
            ...current,
            [Number(zoneMatch[1])]: true,
          }));
        }
        setValidationAttempt((current) => current + 1);
        focusFirstError(firstErrorPath, {
          sectionSelector:
            ".zone-accordion--error, .requirements-section--error",
          delay: 160,
        });
      }
      return isValid;
    };

    const validatePortalDraft = async () => {
      if (!companyPortalMode) return true;
      try {
        await portalZonesDraftValidationSchema.validate(formik.values, {
          abortEarly: false,
        });
        return true;
      } catch (e) {
        setValidatedZoneCount(formik.values.zones_attributes.length);
        if (Array.isArray(e?.inner)) {
          e.inner.forEach((item) => {
            if (!item?.path) return;
            formik.setFieldError(item.path, item.message);
            formik.setFieldTouched(item.path, true, false);
          });
        }
        formik.setTouched(
          {
            zones_attributes: formik.values.zones_attributes.map(() => ({
              name: true,
            })),
          },
          true
        );
        const firstErrorPath = getFirstErrorPath(
          e?.inner?.reduce((acc, item) => {
            if (item?.path) acc[item.path] = item.message;
            return acc;
          }, {}) || { zones_attributes: e?.message }
        );
        const zoneMatch = /^zones_attributes\.(\d+)/.exec(firstErrorPath);
        if (zoneMatch) {
          setExpandedZones((current) => ({
            ...current,
            [Number(zoneMatch[1])]: true,
          }));
        }
        setValidationAttempt((current) => current + 1);
        focusFirstError(firstErrorPath || "zones_attributes.0.name", {
          sectionSelector: ".zone-accordion--error",
          delay: 160,
        });
        return false;
      }
    };

    const applyPersistedEvent = (attrs) => {
      if (!attrs || !companyPortalMode) return;
      const nextZones = mergePersistedPortalZonesIntoFormValues(
        formik.values.zones_attributes,
        attrs
      );
      const nextRequirements = mergePersistedPortalRequirementsIntoFormValues(
        formik.values.logistics_requirements_attributes,
        attrs
      );
      const urlFromServer =
        attrs?.zones_image_url ?? attrs?.attributes?.zones_image_url;
      const nextZonesImage =
        typeof urlFromServer === "string"
          ? urlFromServer.trim()
          : formik.values.zones_image_url;
      // Single atomic update + validate so PATCH responses don't leave stale
      // field errors on rows that now match server state (`setFieldValue(..., false)`
      // skips validation).
      formik.setValues(
        {
          ...formik.values,
          zones_attributes: nextZones,
          logistics_requirements_attributes: nextRequirements,
          zones_image_url: nextZonesImage,
        },
        true
      );
      setZoneMapUploadCompleted(
        typeof nextZonesImage === "string" && String(nextZonesImage).trim().length > 0,
      );
      if (typeof nextZonesImage === "string" && nextZonesImage) {
        setZoneMapPreviewDataUrl(null);
      }
    };

    if (registerStepApi) {
      registerStepApi({
        validateContinue,
        validatePortalDraft,
        // Silent check for Submit for Approval: one item when the step isn't filled in
        // (e.g. a draft step never completed), not one per empty field.
        countMissingFields: async () =>
          countFormErrors(await formik.validateForm()) > 0 ? 1 : 0,
        getSubmitIssues: () => [
          (messages[lang] ?? messages.en).events.creatEvent.submissionFailed
            .completeStep,
        ],
        buildPayload: async () => {
          const hadNewFile =
            formik.values.zones_image_url instanceof File;
          if (hadNewFile) {
            setPortalZoneMapSubmitLoading(true);
            setPortalZoneMapUploadPercent(null);
          }
          try {
            const resolvedUrl = await resolvePortalZonesImageUrl(formik.values, {
              onProgress: hadNewFile
                ? (pct) => setPortalZoneMapUploadPercent(pct)
                : undefined,
            });
            if (hadNewFile && resolvedUrl) {
              formik.setFieldValue("zones_image_url", resolvedUrl, false);
            }
            const valuesForPayload = {
              ...formik.values,
              zones_image_url: resolvedUrl ?? formik.values.zones_image_url,
            };
            const base = buildPortalStepThreePayload(valuesForPayload, {
              requirementCatalog: requirementOptions,
            });
            return {
              ...base,
              zones_attributes: [
                ...base.zones_attributes,
                ...deletedZonesRef.current,
              ],
              event_requirements_attributes: [
                ...(base.event_requirements_attributes ?? []),
                ...deletedRequirementsRef.current,
              ],
            };
          } finally {
            if (hadNewFile) {
              setPortalZoneMapSubmitLoading(false);
              setPortalZoneMapUploadPercent(null);
            }
          }
        },
        applyPersistedEvent: companyPortalMode
          ? (attrs) => {
              applyPersistedEvent(attrs);
              deletedZonesRef.current = [];
              deletedRequirementsRef.current = [];
            }
          : undefined,
      });
      return () => registerStepApi(null);
    }

    registerSubmit?.(async () => validateContinue());

    return () => registerSubmit?.(null);
  }, [
    companyPortalMode,
    formik,
    portalZonesDraftValidationSchema,
    registerStepApi,
    registerSubmit,
    requirementOptions,
  ]);

  const showSectionErrors = validationAttempt > 0;
  const isZoneValidated = (zoneIndex) => zoneIndex < validatedZoneCount;

  const zoneMapField = formik.values.zones_image_url;
  const zoneMapPreview =
    zoneMapPreviewDataUrl ||
    (typeof zoneMapField === "string" ? zoneMapField : "");
  const zoneMapFile = zoneMapField instanceof File ? zoneMapField : null;
  const zoneMapUploadBusy = Boolean(
    portalZoneMapSubmitLoading && zoneMapFile,
  );
  const zoneMapProgressLabel =
    zoneMapUploadBusy && typeof portalZoneMapUploadPercent === "number"
      ? `${t.zoneMapUploading} · ${portalZoneMapUploadPercent}%`
      : zoneMapUploadBusy
        ? t.zoneMapUploading
        : null;

  /**
   * Returns the validation error message for a field when either:
   * - the whole section has been attempted (showSectionErrors), or
   * - the individual field has been touched by the user.
   * Passing the result as both `error` and `helperText` to UI field
   * components ensures the red border and message appear consistently
   * with the accordion/panel-level error border.
   */
  const getFieldError = (name) => {
    const error = getIn(formik.errors, name);
    if (!error) return undefined;
    const touched = getIn(formik.touched, name);
    return sectionErrorsApplyTo(name, showSectionErrors, validatedZoneCount) ||
      touched ||
      formik.submitCount > 0
      ? error
      : undefined;
  };

  /** Logistics rows are optional until added; show row errors only after submit attempt. */
  const getRequirementFieldError = (name) => {
    const error = getIn(formik.errors, name);
    return showSectionErrors && error ? error : undefined;
  };

  const requirementsHaveError =
    showSectionErrors &&
    Boolean(
      getIn(formik.errors, "logistics_requirements_attributes") ||
        (requirements || []).some(
          (_row, index) =>
            getIn(
              formik.errors,
              `logistics_requirements_attributes.${index}.requirement_id`,
            ) ||
            getIn(
              formik.errors,
              `logistics_requirements_attributes.${index}.quantity`,
            ),
        ),
    );

  return (
    <FormikProvider value={formik}>
      <form className="zones-logistics-step" onSubmit={formik.handleSubmit}>
        <div className="zones-logistics-step__intro">
          <Typography
            className="zones-logistics-step__title text-heading-xl"
            component="h1"
          >
            {t.pageTitle}
          </Typography>
          <Typography
            className="zones-logistics-step__subtitle text-body-md"
            component="p"
          >
            {t.pageSubtitle}
          </Typography>
        </div>

        <section className="zones-section">
          <div className="zones-logistics-step__section-header">
            <div>
              <UIIcon name="data" size="md" />
              <Typography className="text-label-md" component="h2">
                {t.zoneManagement}
              </Typography>
            </div>
            <UIButton
              type="button"
              size="small"
              variant="outlined"
              startIcon={<Add sx={{ fontSize: 10 }} />}
              onClick={handleAddZone}
              className="outlined-with-bg text-label-xs"
            >
              {t.addNewZone}
            </UIButton>
          </div>

          <div className="zones-section__list">
            {zones.map((zone, zoneIndex) => {
              const selectedManager = companyPortalMode
                ? null
                : managerOptions.find(
                    (option) => String(option.id) === String(zone.manager_id)
                  ) || null;
              const zoneTitle =
                String(zone.name || "").trim() ||
                t.zoneLabel.replace("{number}", zoneIndex + 1);
              const hasAssignedOrganizers = Boolean(zone?.supervisor_id);
              const zoneRemoveDisabled =
                zones.length <= 1 || hasAssignedOrganizers;
              const removeZoneButton = (
                <UIButton
                  className="zone-accordion__remove"
                  type="button"
                  variant="ghost"
                  size="small"
                  disabled={zoneRemoveDisabled}
                  onClick={(event) => {
                    event.stopPropagation();
                    setZonePendingRemoval(zoneIndex);
                  }}
                >
                  <UIIcon name="trash" size="lg" />
                </UIButton>
              );

              return (
                <UIAccordion
                  key={
                    zone.id != null
                      ? `zone-id-${zone.id}`
                      : `zone-idx-${zoneIndex}`
                  }
                  hasCustomizeTitle
                  expanded={Boolean(expandedZones[zoneIndex])}
                  onChange={(nextExpanded) =>
                    setExpandedZones((current) => ({
                      ...current,
                      [zoneIndex]: nextExpanded,
                    }))
                  }
                  classNames={{
                    root: clx("zone-accordion", {
                      "zone-accordion--error":
                        showSectionErrors &&
                        isZoneValidated(zoneIndex) &&
                        getIn(formik.errors, `zones_attributes.${zoneIndex}`),
                    }),
                  }}
                  title={
                    <div className="zone-accordion__header">
                      <div className="zone-accordion__title">
                        <span />
                        <Typography
                          className="text-heading-xxs text-primary-900"
                          component="h3"
                        >
                          {zoneTitle}
                        </Typography>
                      </div>
                      {hasAssignedOrganizers ? (
                        <Tooltip
                          title={t.zoneHasOrganizersTooltip}
                          content={
                            <span className="zone-accordion__remove-wrapper">
                              {removeZoneButton}
                            </span>
                          }
                        />
                      ) : (
                        removeZoneButton
                      )}
                    </div>
                  }
                >
                  <div className="zone-accordion__body">
                    <div
                      className={clx("zones-field-grid", {
                        "zones-field-grid--two": true,
                      })}
                    >
                      <UITextField
                        name={`zones_attributes.${zoneIndex}.name`}
                        label={t.zoneName}
                        placeholder={t.zoneNamePlaceholder}
                        required
                        maxLength={50}
                        error={Boolean(
                          getFieldError(`zones_attributes.${zoneIndex}.name`)
                        )}
                        helperText={getFieldError(
                          `zones_attributes.${zoneIndex}.name`
                        )}
                      />
                      {companyPortalMode && showSupervisorField ? (
                        <UISelect
                          label={t.supervisorName}
                          name={`zones_attributes.${zoneIndex}.supervisor_id`}
                          placeholder={t.supervisorNamePlaceholder}
                          options={getSupervisorOptionsForZone(
                            allSupervisorSelectOptions,
                            zones,
                            zoneIndex,
                          )}
                          value={
                            allSupervisorSelectOptions.find(
                              (option) =>
                                String(option.id) ===
                                String(zones[zoneIndex]?.supervisor_id),
                            ) || null
                          }
                          onChange={(option) => {
                            formik.setFieldValue(
                              `zones_attributes.${zoneIndex}.supervisor_id`,
                              option?.id ?? "",
                              true,
                            );
                            formik.setFieldValue(
                              `zones_attributes.${zoneIndex}.supervisor_name`,
                              option?.name || "",
                              false,
                            );
                          }}
                          isOptionEqualToValue={(option, selected) =>
                            String(option.id) === String(selected?.id)
                          }
                          helperText={getFieldError(
                            `zones_attributes.${zoneIndex}.supervisor_id`,
                          )}
                          error={Boolean(
                            getFieldError(
                              `zones_attributes.${zoneIndex}.supervisor_id`,
                            ),
                          )}
                        />
                      ) : null}
                      {!companyPortalMode ? (
                        <UITextField
                          name={`zones_attributes.${zoneIndex}.supervisor_name`}
                          label={t.supervisorName}
                          placeholder={t.supervisorNamePlaceholder}
                          helperText={getVisibleError(
                            formik,
                            `zones_attributes.${zoneIndex}.supervisor_name`
                          )}
                        />
                      ) : null}
                    </div>

                    {!companyPortalMode && (
                      <>
                        <div className="zones-logistics-step__divider" />

                        <div className="manager-section">
                          <div className="manager-section__title">
                            <UIIcon name="user" size="sm" decorative />
                            <Typography
                              className="text-label-md"
                              component="span"
                            >
                              {t.managerInformation}
                            </Typography>
                          </div>
                          <div className="zones-field-grid zones-field-grid--three">
                            <UISelect
                              name={`zones_attributes.${zoneIndex}.manager_id`}
                              label={t.zoneManager}
                              placeholder={t.zoneManagerPlaceholder}
                              options={managerOptions}
                              value={selectedManager}
                              onChange={(manager) =>
                                handleManagerChange(zoneIndex, manager)
                              }
                              isOptionEqualToValue={(option, selected) =>
                                String(option.id) === String(selected?.id)
                              }
                            />
                            <UITextField
                              name={`zones_attributes.${zoneIndex}.manager_email`}
                              label={t.managerEmail}
                              placeholder={t.managerEmailPlaceholder}
                              error={Boolean(
                                getFieldError(
                                  `zones_attributes.${zoneIndex}.manager_email`,
                                )
                              )}
                              helperText={getFieldError(
                                `zones_attributes.${zoneIndex}.manager_email`,
                              )}
                            />
                            <UITextField
                              name={`zones_attributes.${zoneIndex}.manager_phone`}
                              label={t.managerPhone}
                              placeholder={t.managerPhonePlaceholder}
                            />
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </UIAccordion>
              );
            })}
          </div>

          <UIButton
            className="add-more text-label-sm"
            type="button"
            variant="text"
            startIcon={<Add sx={{ fontSize: 12 }} />}
            onClick={handleAddZone}
          >
            {t.addAnotherZone}
          </UIButton>
        </section>

        {companyPortalMode && (
          <section className="zone-map-section">
            <div className="zones-logistics-step__section-header">
              <div>
                <FmdGoodOutlinedIcon sx={{ fontSize: 18 }} />
                <Typography className="text-label-md" component="h2">
                  {t.zoneMap} <span className="ui-field__required">*</span>
                </Typography>
              </div>
            </div>
            <UIUpload
              name="zones_image_url"
              uploadText={t.zoneMapUploadText}
              hint={t.zoneMapFormatsHint}
              variant="dropzone"
              preview={zoneMapPreview}
              previewLabel={
                typeof zoneMapField === "string" && zoneMapField
                  ? zoneMapField.split("/").pop()?.split("?")[0] ||
                    zoneMapField
                  : undefined
              }
              file={zoneMapFile}
              showRemove
              required
              accept=".png,.jpg,.jpeg,.pdf,image/png,image/jpeg,application/pdf"
              acceptedTypes={[
                "image/jpeg",
                "image/png",
                "image/jpg",
                "application/pdf",
              ]}
              maxSizeBytes={10 * 1024 * 1024}
              maxSizeLabel="10MB"
              error={Boolean(getFieldError("zones_image_url"))}
              helperText={getFieldError("zones_image_url")}
              isUploading={zoneMapUploadBusy}
              uploadCompleted={zoneMapUploadCompleted}
              successLabel={shared.uploadedSuccessfully}
              removeAriaLabel={shared.removeFile}
              uploadProgress={
                typeof portalZoneMapUploadPercent === "number"
                  ? portalZoneMapUploadPercent
                  : undefined
              }
              uploadProgressLabel={zoneMapProgressLabel}
              invalidTypeMessage={t.zoneMapInvalidType}
              invalidSizeMessage={t.zoneMapInvalidSize}
              onChange={(file, preview) => {
                formik.setFieldTouched("zones_image_url", true, false);
                if (!file) {
                  formik.setFieldValue("zones_image_url", "");
                  setZoneMapPreviewDataUrl(null);
                  setZoneMapUploadCompleted(false);
                  return;
                }
                formik.setFieldValue("zones_image_url", file);
                setZoneMapPreviewDataUrl(
                  typeof preview === "string" ? preview : null,
                );
                setZoneMapUploadCompleted(false);
              }}
            />
          </section>
        )}

        <section
          className="requirements-section"
        >
          <div className="zones-logistics-step__section-header">
            <div>
              <Inventory2Outlined fontSize="small" />
              <Typography className="text-label-md" component="h2">
                {t.logisticalRequirements}
              </Typography>
            </div>
            <UIButton
              type="button"
              size="small"
              variant="outlined"
              startIcon={<Add />}
              onClick={handleAddRequirement}
              className="outlined-with-bg text-label-xs"
            >
              {t.addItem}
            </UIButton>
          </div>

          {requirements.length > 0 && (
          <Card
            title={
              <div>
                <div className="requirements-panel__head">
                  <Typography className="text-label-sm" component="span">
                    {t.requirementName}
                    {requirements.length > 0 ? (
                      <span className="text-danger"> *</span>
                    ) : null}
                  </Typography>
                  <Typography className="text-label-sm" component="span">
                    {t.quantity}
                    {requirements.length > 0 ? (
                      <span className="text-danger"> *</span>
                    ) : null}
                  </Typography>
                  <Typography className="text-label-sm" component="span">
                    {t.explanationNotes}
                  </Typography>
                  <span />
                </div>
              </div>
            }
            classNames={{
              root: clx("requirements-panel", {
                "requirements-panel--error": requirementsHaveError,
              }),
            }}
          >
            <div className="requirements-panel__body">
              {requirements.map((_requirementRow, requirementIndex) => {
                const rowRequirementOptions = getAvailableRequirementOptions(
                  requirements,
                  requirementIndex,
                  requirementOptions,
                );
                const selectedRequirementOption = getSelectedRequirementOption(
                  requirements,
                  requirementIndex,
                  requirementOptions,
                  rowRequirementOptions,
                );

                return (
                  <div
                    className="requirements-panel__row"
                    key={`requirement-${requirementIndex}`}
                  >
                    <UISelect
                      key={`requirement-select-${requirementIndex}-${selectedRequirementKeys}`}
                      name={`logistics_requirements_attributes.${requirementIndex}.requirement_id`}
                      placeholder={t.requirementNamePlaceholder}
                      options={rowRequirementOptions}
                      disabled={requirementOptions.length === 0}
                      value={selectedRequirementOption}
                      onChange={(option) => {
                        const nextRequirements = requirements.map((row, index) =>
                          index === requirementIndex
                            ? {
                                ...row,
                                requirement_id: option?.id ?? "",
                                name: option?.name || "",
                              }
                            : row,
                        );
                        formik.setFieldValue(
                          "logistics_requirements_attributes",
                          nextRequirements,
                          true,
                        );
                      }}
                      isOptionEqualToValue={(option, selected) =>
                        String(option.id) === String(selected?.id)
                      }
                      helperText={
                        requirementOptions.length === 0
                          ? t.requirementsCatalogEmpty
                          : getRequirementFieldError(
                              `logistics_requirements_attributes.${requirementIndex}.requirement_id`,
                            )
                      }
                      error={
                        requirementOptions.length > 0 &&
                        Boolean(
                          getRequirementFieldError(
                            `logistics_requirements_attributes.${requirementIndex}.requirement_id`,
                          ),
                        )
                      }
                    />
                    <UINumberField
                      name={`logistics_requirements_attributes.${requirementIndex}.quantity`}
                      placeholder="0"
                      min={0}
                      step={1}
                      error={Boolean(
                        getRequirementFieldError(
                          `logistics_requirements_attributes.${requirementIndex}.quantity`,
                        ),
                      )}
                      helperText={getRequirementFieldError(
                        `logistics_requirements_attributes.${requirementIndex}.quantity`,
                      )}
                    />
                    <UITextarea
                      name={`logistics_requirements_attributes.${requirementIndex}.notes`}
                      placeholder={t.explanationNotesPlaceholder}
                      minRows={1}
                      maxRows={1}
                      maxLength={100}
                      helperText={getVisibleError(
                        formik,
                        `logistics_requirements_attributes.${requirementIndex}.notes`
                      )}
                    />
                    <UIButton
                      className="requirements-panel__remove"
                      type="button"
                      variant="text"
                      color="inherit"
                      onClick={() =>
                        setRequirementPendingRemoval(requirementIndex)
                      }
                    >
                      <UIIcon name="trash" size="sm" />
                    </UIButton>
                  </div>
                );
              })}
            </div>
            <div className="requirements-panel__footer">
              <UIButton
                className="requirements-panel__add text-label-sm"
                type="button"
                variant="text"
                startIcon={<Add sx={{ fontSize: 12 }} />}
                onClick={handleAddRequirement}
              >
                {t.addNewRequirements}
              </UIButton>
            </div>
          </Card>
          )}
        </section>

        {isEditMode && (
          <input type="hidden" name="mode" value="edit" readOnly />
        )}

        <RemoveModal
          open={zonePendingRemoval !== null}
          onClose={() => setZonePendingRemoval(null)}
          onConfirm={handleRemoveZone}
          title={t.removeZone}
          message={t.removeZoneMessage}
          confirmLabel={t.removeZone}
        />
        <RemoveModal
          open={requirementPendingRemoval !== null}
          onClose={() => setRequirementPendingRemoval(null)}
          onConfirm={handleRemoveRequirement}
          title={t.removeRequirement}
          message={t.removeRequirementMessage}
          confirmLabel={t.removeRequirement}
        />
      </form>
    </FormikProvider>
  );
};

ZonesAndLogisticalRequirements.propTypes = {
  isEditMode: PropTypes.bool,
  registerSubmit: PropTypes.func,
  registerStepApi: PropTypes.func,
  companyPortalMode: PropTypes.bool,
  initialEvent: PropTypes.object,
  requirementsOptions: PropTypes.arrayOf(PropTypes.object),
  supervisorsOptions: PropTypes.arrayOf(PropTypes.object),
  requestsPortal: PropTypes.oneOf(["admin", "company"]),
  onDeleteRequirement: PropTypes.func,
};

ZonesAndLogisticalRequirements.defaultProps = {
  isEditMode: false,
  registerSubmit: undefined,
  registerStepApi: undefined,
  companyPortalMode: false,
  initialEvent: undefined,
  requirementsOptions: [],
  supervisorsOptions: [],
  requestsPortal: "admin",
  onDeleteRequirement: undefined,
};

export default ZonesAndLogisticalRequirements;
